/**
 * Copyright 2025 Lumavec Group LLC
 *
 * ConflictResolutionPanel - Shows list of conflicts and their resolution status
 */

import React from 'react';
import {
  AlertTriangle,
  Check,
  Loader,
  Clock,
  XCircle,
  FileText,
  Sparkles,
  GitMerge
} from 'lucide-react';
import { ConflictResolution, ResolutionStatus } from './types';

interface ConflictResolutionPanelProps {
  conflicts: ConflictResolution[];
  selectedIndex: number | null;
  onSelectConflict: (index: number) => void;
  onResolveAllWithAI: () => void;
  isResolvingAll: boolean;
  autoApplyThreshold: number;
}

export function ConflictResolutionPanel({
  conflicts,
  selectedIndex,
  onSelectConflict,
  onResolveAllWithAI,
  isResolvingAll,
  autoApplyThreshold
}: ConflictResolutionPanelProps) {
  const pendingCount = conflicts.filter(c => c.status === 'pending').length;
  const resolvedCount = conflicts.filter(c => c.status === 'resolved').length;
  const deferredCount = conflicts.filter(c => c.status === 'deferred').length;
  const errorCount = conflicts.filter(c => c.status === 'error').length;

  const getStatusIcon = (status: ResolutionStatus) => {
    switch (status) {
      case 'pending':
        return <AlertTriangle size={14} className="text-orange-500" />;
      case 'resolving':
        return <Loader size={14} className="text-blue-500 animate-spin" />;
      case 'resolved':
        return <Check size={14} className="text-green-500" />;
      case 'deferred':
        return <Clock size={14} className="text-gray-500" />;
      case 'error':
        return <XCircle size={14} className="text-red-500" />;
    }
  };

  const getStatusLabel = (conflict: ConflictResolution) => {
    if (conflict.status === 'resolved' && conflict.method) {
      const methodLabels: Record<string, string> = {
        local: 'Kept local',
        remote: 'Kept remote',
        ai: `AI (${Math.round((conflict.aiResult?.confidence || 0) * 100)}%)`,
        manual: 'Manual edit'
      };
      return methodLabels[conflict.method] || 'Resolved';
    }
    
    const statusLabels: Record<ResolutionStatus, string> = {
      pending: 'Pending',
      resolving: 'Resolving...',
      resolved: 'Resolved',
      deferred: 'Deferred',
      error: 'Error'
    };
    return statusLabels[conflict.status];
  };

  const getEntityTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      'issue': 'Issue',
      'user-story': 'User Story',
      'epic': 'Epic'
    };
    return labels[type] || type;
  };

  const getEntityTypeColor = (type: string) => {
    const colors: Record<string, string> = {
      'issue': 'bg-purple-100 text-purple-800',
      'user-story': 'bg-blue-100 text-blue-800',
      'epic': 'bg-green-100 text-green-800'
    };
    return colors[type] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="flex flex-col h-full">
      {/* Header with summary */}
      <div className="p-4 bg-orange-50 border-b border-orange-200">
        <div className="flex items-center gap-2 mb-2">
          <GitMerge size={18} className="text-orange-600" />
          <h3 className="font-semibold text-orange-900">Merge Conflicts</h3>
        </div>
        
        <div className="flex flex-wrap gap-2 text-xs">
          {pendingCount > 0 && (
            <span className="px-2 py-1 bg-orange-100 text-orange-800 rounded-full">
              {pendingCount} pending
            </span>
          )}
          {resolvedCount > 0 && (
            <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full">
              {resolvedCount} resolved
            </span>
          )}
          {deferredCount > 0 && (
            <span className="px-2 py-1 bg-gray-100 text-gray-800 rounded-full">
              {deferredCount} deferred
            </span>
          )}
          {errorCount > 0 && (
            <span className="px-2 py-1 bg-red-100 text-red-800 rounded-full">
              {errorCount} failed
            </span>
          )}
        </div>
      </div>

      {/* Resolve All with AI button */}
      {pendingCount > 0 && (
        <div className="p-3 border-b border-gray-200">
          <button
            onClick={onResolveAllWithAI}
            disabled={isResolvingAll}
            className="w-full btn-primary text-sm py-2.5 flex items-center justify-center gap-2"
          >
            {isResolvingAll ? (
              <>
                <Loader size={14} className="animate-spin" />
                Resolving with AI...
              </>
            ) : (
              <>
                <Sparkles size={14} />
                Resolve All with AI
              </>
            )}
          </button>
          <p className="text-xs text-gray-500 mt-2 text-center">
            Auto-applies resolutions with ≥{Math.round(autoApplyThreshold * 100)}% confidence
          </p>
        </div>
      )}

      {/* Conflict list */}
      <div className="flex-1 overflow-auto">
        {conflicts.length === 0 ? (
          <div className="flex items-center justify-center h-32 text-gray-500">
            <div className="text-center">
              <Check size={32} className="mx-auto text-green-500 mb-2" />
              <p className="text-sm">No conflicts</p>
            </div>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {conflicts.map((conflict, index) => (
              <button
                key={`${conflict.conflict.entityId}-${index}`}
                onClick={() => onSelectConflict(index)}
                className={`w-full text-left p-3 hover:bg-gray-50 transition-colors ${
                  selectedIndex === index ? 'bg-blue-50 border-l-4 border-blue-600' : ''
                }`}
              >
                <div className="flex items-start gap-2">
                  {getStatusIcon(conflict.status)}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`text-xs font-medium px-2 py-0.5 rounded ${getEntityTypeColor(conflict.conflict.entityType)}`}>
                        {getEntityTypeLabel(conflict.conflict.entityType)}
                      </span>
                      <span className="text-sm font-medium text-gray-900 truncate">
                        {conflict.conflict.entityId}
                      </span>
                    </div>
                    
                    <div className="flex items-center gap-1 text-xs text-gray-500">
                      <FileText size={12} />
                      <span className="truncate">{conflict.conflict.filePath.split('/').pop()}</span>
                    </div>
                    
                    <div className="mt-1">
                      <span className={`text-xs ${
                        conflict.status === 'resolved' ? 'text-green-600' :
                        conflict.status === 'error' ? 'text-red-600' :
                        conflict.status === 'deferred' ? 'text-gray-500' :
                        'text-orange-600'
                      }`}>
                        {getStatusLabel(conflict)}
                      </span>
                      
                      {conflict.error && (
                        <span className="text-xs text-red-500 ml-2" title={conflict.error}>
                          - {conflict.error.substring(0, 30)}...
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Summary footer */}
      {resolvedCount === conflicts.length && conflicts.length > 0 && (
        <div className="p-3 bg-green-50 border-t border-green-200">
          <div className="flex items-center gap-2 text-green-800 text-sm">
            <Check size={16} />
            <span>All conflicts resolved!</span>
          </div>
        </div>
      )}
    </div>
  );
}
